import React, {FC, useEffect, useState} from "react"
import styled from "styled-components"
import {Grid} from "react-bootstrap"
import {RouteComponentProps} from "react-router-dom"
import {CSSTransition, TransitionGroup} from "react-transition-group"

import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {DealBrand, DealBrandSearchResponse, DealListSort} from "@irati/types"
import Loading from "@irati/ui/src/Loading"
import dealBrandService from "@irati/deal-brands/services/dealBrands"
import tracking from "@irati-tracking/perks/perks"
import {useDealList} from "@irati/deals/hooks"
import DealBrandResults from "@irati-web/search/src/components/DealBrandResults"
import SearchBrands from "@irati/deals/apollo/query/SearchBrands"
import {Col, Hr, Row} from "@irati/ui/src/Layout"
import Spacing from "@irati/ui/src/Spacing"
import DealPages from "../../enums/DealPages"
import BrandBanner from "../../components/DealBrand/BrandBanner"
import DealCard from "../../components/Deal/DealCard"

interface Props extends InjectIntl, RouteComponentProps<{dealBrandSlug: string}> {}

const DealBrandPage: FC<Props> = (props) => {
  const [dealBrand, setDealBrand] = useState<DealBrand>()
  const [load, setLoad] = useState<Promise<void>>()

  useEffect(() => {
    const getBrand = async (): Promise<void> => {
      const brand = await dealBrandService.getBySlug({slug: props.match.params.dealBrandSlug})
      setDealBrand(brand)
    }
    getBrand()

    setLoad(getBrand)
    tracking.viewBrand(dealBrand?.uuid!, dealBrand?.label!)
  }, [props.match.params.dealBrandSlug])

  const deals = useDealList(
    {
      brandIds: [dealBrand?.uuid!],
      limit: 50,
      sort: DealListSort.Popularity
    },
    {skip: !dealBrand}
  )

  const dealListTagIds = deals?.data?.results.map((el) => el.brandTagIds).flat()
  const uniqBrandTagIds = Array.from(new Set(dealListTagIds)).filter((n) => n)

  const variables = {
    brandTagIds: uniqBrandTagIds,
    excludeBrandId: dealBrand?.uuid
  }

  return (
    <Loading load={load} initLoad>
      <BrandBanner dealBrand={dealBrand!} totalResultsCount={deals.data?.total!} />
      <Grid>
        <Row>
          <TransitionGroup component={FlexComponent}>
            {deals.data?.results.map((deal) => (
              <CSSTransition key={deal.uuid} classNames="dealList" timeout={{enter: 400, exit: 300}}>
                <Col md={3} sm={6} xs={12}>
                  <Spacing marginBottom={4}>
                    <DealCard deal={deal} page={DealPages.BRAND} isFavouriteDeal={false} />
                  </Spacing>
                </Col>
              </CSSTransition>
            ))}
          </TransitionGroup>
        </Row>

        {variables.brandTagIds.length > 0 && variables.excludeBrandId && (
          <>
            <Hr marginTop={6} marginBottom={6} />
            <SearchBrands variables={variables}>
              {({data, error}) => {
                if (error || !data?.dealsBrands) return null

                let dealBrands
                if (data?.dealsBrands instanceof Array) {
                  dealBrands = data?.dealsBrands?.map((b: DealBrandSearchResponse) => b)
                }

                return <DealBrandResults dealBrands={dealBrands} wasAccessedFromRelevantBrands />
              }}
            </SearchBrands>
          </>
        )}
      </Grid>
    </Loading>
  )
}

export default injectIntl(DealBrandPage)

const FlexComponent = styled.div`
  display: flex;
  flex-wrap: wrap;
`
